import React, { useState, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { API_BASE_URL } from "./config";
import CanvasEditor from "./CanvasScreen";
import LoginScreen from "./LoginScreen";
import RegisterScreen from "./RegisterScreen";
import PricingScreen from "./PricingScreen";
import OnboardingScreen from "./OnboardingScreen";
import SettingsScreen from "./SettingsScreen";

function App() {
  const [currentView, setCurrentView] = useState('onboarding');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [userLineConfig, setUserLineConfig] = useState({ token: "", userId: "" });
  const [showSettings, setShowSettings] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // 監聽 Firebase 登入狀態（含重新整理後恢復 session）
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAuthReady(true);
      if (user) {
        setIsLoggedIn(true);
        setCurrentView('app');
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const d = snap.data();
            setUserLineConfig({
              token: d.lineToken || "",
              userId: d.testUserId || "",
            });
          } else {
            setUserLineConfig({ token: "", userId: "" });
          }
        } catch (err) {
          console.error("載入 LINE 設定失敗:", err);
          setUserLineConfig({ token: "", userId: "" });
        }
      } else {
        setIsLoggedIn(false);
        setCurrentView('onboarding');
        setUserLineConfig({ token: "", userId: "" });
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSettingsSaved = (config) => {
    setUserLineConfig(config);
  };

  // 1. 登入成功
  const handleLogin = () => {
    setIsLoggedIn(true);
    setCurrentView('app'); 
  };

  // 2. 註冊成功 -> 進入導覽 (原本是進入 pricing)
  const handleRegisterSuccess = () => {
    setIsLoggedIn(true);
    // ✅ 修改：註冊完成後，直接進入新手導覽
    setCurrentView('onboarding');
  };

  // 3. 選擇方案
  const handleSelectPlan = async (planId) => {
    if (planId === 'basic') {
      setCurrentView('app');
    } else {
      await handlePayment(planId);
    }
  };

  // 4. 關閉方案頁
  const handleClosePricing = () => {
    if (isLoggedIn) {
      setCurrentView('app');
    } else {
      setCurrentView('login');
    }
  };

  // 5. 導覽結束 -> 進入主畫面 (因為已經登入了)
  const handleFinishOnboarding = () => {
    if (isLoggedIn) {
      setCurrentView('app');
    } else {
      setCurrentView('login');
    }
  };

  // 6. 登出（同時清除 Firebase  session）
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setCurrentView('login');
    } catch (err) {
      console.error('登出失敗:', err);
      setIsLoggedIn(false);
      setCurrentView('login');
    }
  };

  const handlePayment = async (planId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: 'guest', plan: planId }),
      });
      const data = await response.json();
      if (data.html) {
        const div = document.createElement('div');
        div.innerHTML = data.html;
        document.body.appendChild(div);
        const form = div.querySelector('form');
        const script = div.querySelector('script');
        if (form) form.submit();
        else if (script) eval(script.innerText);
      } else {
        alert('訂單建立失敗');
      }
    } catch (error) {
      console.error('Payment Error:', error);
      alert('無法連接付款伺服器');
    }
  };

  const renderScreen = () => {
    // 等待 Firebase 確認登入狀態，避免閃爍
    if (!isAuthReady) {
      return (
        <div style={{
          width: "100vw", height: "100vh",
          display: "flex", justifyContent: "center", alignItems: "center",
          background: "var(--bg-main)", color: "#aaa", fontSize: 14
        }}>
          載入中...
        </div>
      );
    }

    switch (currentView) {
      case 'login':
        return (
          <LoginScreen 
            onLogin={handleLogin} 
            onGoToRegister={() => setCurrentView('register')}
            onGoToOnboarding={() => setCurrentView('onboarding')}
          />
        );
      
      case 'register':
        return <RegisterScreen onRegisterSuccess={handleRegisterSuccess} onGoToLogin={() => setCurrentView('login')} />;
      
      case 'pricing':
        return (
          <PricingScreen 
            onSelectPlan={handleSelectPlan} 
            onClose={handleClosePricing}
          />
        );
      
      case 'onboarding':
        return <OnboardingScreen onFinish={handleFinishOnboarding} />;
      
      case 'app':
        return (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "var(--bg-main)",
              color: "var(--text-primary)",
              overflow: "hidden",
            }}
          >
            {/* 頂部 Bar */}
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 20px",
                background: "#06C755",
                borderBottom: "1px solid rgba(0,0,0,0.1)",
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setShowSidebar(true)}
                style={{
                  width: 40,
                  height: 40,
                  background: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 8,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                }}
              >
                <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 1 }} />
                <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 1 }} />
                <span style={{ width: 18, height: 2, background: "#fff", borderRadius: 1 }} />
              </button>

              <div style={{ fontSize: 18, fontWeight: 600, color: "#fff" }}>
                LINE Flex 編輯器
              </div>

              <button
                disabled
                title="即將推出"
                style={{
                  padding: "8px 16px",
                  background: "#2a2a2a",
                  color: "#666",
                  border: "1px solid #444",
                  borderRadius: 6,
                  fontSize: 13,
                  cursor: "not-allowed",
                  opacity: 0.8,
                }}
              >
                升級方案
              </button>
            </header>

            {/* 主內容區 */}
            <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>

            {/* 左側滑出選單 */}
            {showSidebar && (
              <>
                <div
                  onClick={() => setShowSidebar(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 1098,
                    animation: "fadeIn 0.2s ease-out",
                  }}
                />
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: 240,
                    height: "100vh",
                    background: "var(--bg-panel)",
                    borderRight: "1px solid var(--border-color)",
                    boxShadow: "4px 0 20px rgba(0,0,0,0.3)",
                    zIndex: 1099,
                    display: "flex",
                    flexDirection: "column",
                    padding: "60px 0 20px",
                    animation: "slideInLeft 0.25s ease-out",
                  }}
                >
                  <button
                    onClick={() => { setShowSidebar(false); setShowSettings(true); }}
                    style={{
                      width: "calc(100% - 24px)",
                      margin: "0 12px 8px",
                      padding: "12px 16px",
                      background: "#333",
                      color: "#fff",
                      border: "1px solid #555",
                      borderRadius: 6,
                      fontSize: 14,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    設定
                  </button>
                  <button
                    onClick={() => { setShowSidebar(false); setCurrentView('onboarding'); }}
                    style={{
                      width: "calc(100% - 24px)",
                      margin: "0 12px 8px",
                      padding: "12px 16px",
                      background: "#333",
                      color: "#fff",
                      border: "1px solid #555",
                      borderRadius: 6,
                      fontSize: 14,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    新手導覽
                  </button>
                  <button
                    onClick={() => { setShowSidebar(false); handleLogout(); }}
                    style={{
                      width: "calc(100% - 24px)",
                      margin: "0 12px 8px",
                      padding: "12px 16px",
                      background: "#333",
                      color: "#fff",
                      border: "1px solid #555",
                      borderRadius: 6,
                      fontSize: 14,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    登出
                  </button>
                </div>
              </>
            )}

            <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <CanvasEditor userLineConfig={userLineConfig} />
            </div>
            {showSettings && (
              <SettingsScreen
                onClose={() => setShowSettings(false)}
                onSaved={handleSettingsSaved}
              />
            )}
            </div>
          </div>
        );
        
      default:
        return <LoginScreen onLogin={handleLogin} />;
    }
  };

  const Footer = () => (
    <footer
      style={{
        padding: "16px 20px",
        background: "var(--bg-panel)",
        borderTop: "1px solid var(--border-color)",
        fontSize: 12,
        color: "#888",
        textAlign: "center",
        flexShrink: 0,
      }}
    >
      <div>聯絡窗口／合作邀約：curamake@gmail.com</div>
      <div style={{ marginTop: 4 }}>CuraMake 庫拉科技股份有限公司</div>
    </footer>
  );

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto" }}>
        {renderScreen()}
      </div>
      <Footer />
    </div>
  );
}

export default App;